import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Sparkles, Upload, X, Icon } from '../../../ui/Icon';
import Spinner from '../../../ui/Spinner';
import { importPlanCounts, type ExtractedTripPlan } from '../../../../api/import.api';
import type { DocumentImport } from '../useDocumentImport';

interface Props {
  docs: DocumentImport;
  complementWithAi: boolean;
  onComplementChange: (value: boolean) => void;
}

function PlanChips({ plan }: { plan: ExtractedTripPlan }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {importPlanCounts(plan).map(c => (
        <span key={c.key} className="text-[12px] font-medium px-2.5 py-1 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
          {t(c.key, { count: c.count })}
        </span>
      ))}
    </div>
  );
}

export default function DocumentsStep({ docs, complementWithAi, onComplementChange }: Props) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-4">
      {docs.importing ? (
        <div className="flex flex-col items-center gap-3 py-8 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 text-slate-500 text-sm font-medium">
          <span className="flex items-center gap-2"><Spinner size="sm" />{t('dashboard.newTripWizard.importing')}</span>
          {docs.partial && <PlanChips plan={docs.partial} />}
          {docs.progress && docs.progress.total > 1 && (
            <div className="w-full max-w-[240px] space-y-1">
              <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${(docs.progress.current / docs.progress.total) * 100}%` }} />
              </div>
              <p className="text-[11px] text-slate-400 text-center">{t('import.docProgress', docs.progress)}</p>
            </div>
          )}
        </div>
      ) : docs.plan ? (
        <div className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-4 space-y-3">
          <p className="text-[14px] font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.newTripWizard.documentsFound')}</p>
          <PlanChips plan={docs.plan} />
          <button type="button" className="text-[13px] font-medium text-slate-500 hover:text-red-500 cursor-pointer" onClick={docs.clear}>
            {t('dashboard.newTripWizard.documentsRemove')}
          </button>
        </div>
      ) : (
        <>
          <button
            type="button" onClick={() => fileInputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-teal-400 hover:text-teal-600 dark:hover:text-teal-300 transition-colors cursor-pointer"
          >
            <Icon as={Upload} size={22} />
            <span className="text-[14px] font-semibold">
              {docs.staged.length > 0 ? t('import.addMore') : t('dashboard.newTripWizard.importFromDocument')}
            </span>
            <span className="text-[12px] text-slate-400">{t('import.formats')}</span>
          </button>
          {docs.staged.length > 0 && (
            <ul className="space-y-1.5">
              {docs.staged.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                  <Icon as={FileText} size={14} className="text-slate-400 shrink-0" />
                  <span className="flex-1 truncate text-[13px] text-slate-600 dark:text-slate-300">{f.name}</span>
                  <button type="button" onClick={() => docs.removeFile(i)} className="text-slate-400 hover:text-red-500 cursor-pointer" aria-label={t('common.remove')}>
                    <Icon as={X} size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <input
        ref={fileInputRef} type="file" multiple className="hidden"
        accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,image/*"
        onChange={e => { docs.addFiles(e.target.files); e.target.value = ''; }}
      />
      {docs.error && <p className="text-[13px] text-red-500">{docs.error}</p>}

      <label className="flex items-start gap-3 rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-4 cursor-pointer">
        <input
          type="checkbox" className="mt-1 accent-teal-600 w-4 h-4"
          checked={complementWithAi} onChange={e => onComplementChange(e.target.checked)}
        />
        <span>
          <span className="flex items-center gap-1.5 text-[14px] font-semibold text-slate-900 dark:text-slate-100">
            <Icon as={Sparkles} size={14} className="text-teal-500" />
            {t('dashboard.newTripWizard.complementAi')}
          </span>
          <span className="block text-[13px] text-slate-500 dark:text-slate-400 mt-0.5">{t('dashboard.newTripWizard.complementAiHint')}</span>
        </span>
      </label>
    </div>
  );
}
